/**
 * REST client helpers for the Slice 2 artifact endpoints.
 *
 * Helpers throw `ArtifactStorageDisabledError` when the server responds with
 * 400 and `code: 'storage_disabled'` so the list view can swap in the
 * disabled-storage banner instead of a generic error.
 */
import { buildBackendUrl } from '@/lib/auth-routing';
import type {
  Artifact,
  ArtifactActivity,
  ArtifactActivityListResponse,
  ArtifactExternalRef,
  ArtifactExternalRefListResponse,
  ArtifactLink,
  ArtifactLinkListResponse,
  ArtifactListResponse,
  ArtifactResponse,
  ArtifactScope,
  ArtifactSnapshot,
  ArtifactSnapshotListResponse,
  ArtifactStatus,
  ArtifactType,
  CreateArtifactBody,
  CreateArtifactExternalRefBody,
  CreateArtifactLinkBody,
  CreateArtifactSnapshotBody,
  PatchArtifactBody,
  RevokeArtifactSnapshotBody,
  SupersedeAdrBody,
  UpdateArtifactBody,
} from './api';

export class ArtifactStorageDisabledError extends Error {
  readonly code = 'storage_disabled' as const;
  constructor(message = 'Artifact storage is disabled') {
    super(message);
    this.name = 'ArtifactStorageDisabledError';
  }
}

export interface ArtifactListFilters {
  type?: ArtifactType;
  scope?: ArtifactScope;
  status?: ArtifactStatus;
  ownerId?: string;
  search?: string;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
}

function buildQuery(filters: ArtifactListFilters): string {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.status) params.set('status', filters.status);
  if (filters.ownerId) params.set('ownerId', filters.ownerId);
  if (filters.search) params.set('search', filters.search);
  if (filters.includeArchived) params.set('includeArchived', 'true');
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function readApiError(res: Response): Promise<{ message: string; code?: string }> {
  try {
    const body = (await res.json()) as { error?: string; code?: string };
    return { message: body.error ?? `API error ${String(res.status)}`, code: body.code };
  } catch {
    return { message: `API error ${String(res.status)}` };
  }
}

async function fetchOrThrow(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(buildBackendUrl(url), {
    ...init,
    credentials: init?.credentials ?? 'include',
  });
  if (!res.ok) {
    const { message, code } = await readApiError(res);
    if (code === 'storage_disabled') throw new ArtifactStorageDisabledError(message);
    const err = new Error(message) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res;
}

export async function listArtifacts(
  codebaseId: string,
  filters: ArtifactListFilters
): Promise<ArtifactListResponse> {
  const res = await fetchOrThrow(
    `/api/codebases/${encodeURIComponent(codebaseId)}/artifacts${buildQuery(filters)}`
  );
  return (await res.json()) as ArtifactListResponse;
}

export async function getArtifact(artifactId: string): Promise<Artifact> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}`);
  const body = (await res.json()) as ArtifactResponse;
  return body.artifact;
}

export async function createArtifact(
  codebaseId: string,
  body: CreateArtifactBody
): Promise<Artifact> {
  const res = await fetchOrThrow(`/api/codebases/${encodeURIComponent(codebaseId)}/artifacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as ArtifactResponse;
  return data.artifact;
}

export interface ArtifactActivityListOptions {
  limit?: number;
  offset?: number;
}

export async function listSnapshots(artifactId: string): Promise<ArtifactSnapshot[]> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/snapshots`);
  const body = (await res.json()) as ArtifactSnapshotListResponse;
  return body.snapshots;
}

export async function listActivity(
  artifactId: string,
  options: ArtifactActivityListOptions = {}
): Promise<ArtifactActivity[]> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  const qs = params.toString();
  const res = await fetchOrThrow(
    `/api/artifacts/${encodeURIComponent(artifactId)}/activity${qs ? `?${qs}` : ''}`
  );
  const body = (await res.json()) as ArtifactActivityListResponse;
  return body.activity;
}

export async function patchArtifact(
  artifactId: string,
  body: PatchArtifactBody
): Promise<Artifact> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as ArtifactResponse;
  return data.artifact;
}

export async function deleteArtifact(artifactId: string): Promise<{ deleted: boolean }> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}`, {
    method: 'DELETE',
  });
  return (await res.json()) as { deleted: boolean };
}

export async function createSnapshot(
  artifactId: string,
  body: CreateArtifactSnapshotBody
): Promise<ArtifactSnapshot> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/snapshots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { snapshot: ArtifactSnapshot };
  return data.snapshot;
}

export async function approveSnapshot(snapshotId: string): Promise<void> {
  await fetchOrThrow(`/api/artifact-snapshots/${encodeURIComponent(snapshotId)}/approve`, {
    method: 'POST',
  });
}

export async function revokeSnapshot(
  snapshotId: string,
  body: RevokeArtifactSnapshotBody
): Promise<void> {
  await fetchOrThrow(`/api/artifact-snapshots/${encodeURIComponent(snapshotId)}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function listLinks(artifactId: string): Promise<ArtifactLink[]> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/links`);
  const body = (await res.json()) as ArtifactLinkListResponse;
  return body.links;
}

export async function createLink(
  artifactId: string,
  body: CreateArtifactLinkBody
): Promise<ArtifactLink> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { link: ArtifactLink };
  return data.link;
}

export async function deleteLink(linkId: string): Promise<{ deleted: boolean }> {
  const res = await fetchOrThrow(`/api/artifact-links/${encodeURIComponent(linkId)}`, {
    method: 'DELETE',
  });
  return (await res.json()) as { deleted: boolean };
}

export async function listExternalRefs(artifactId: string): Promise<ArtifactExternalRef[]> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/external-refs`);
  const body = (await res.json()) as ArtifactExternalRefListResponse;
  return body.externalRefs;
}

export async function createExternalRef(
  artifactId: string,
  body: CreateArtifactExternalRefBody
): Promise<ArtifactExternalRef> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/external-refs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { externalRef: ArtifactExternalRef };
  return data.externalRef;
}

export async function deleteExternalRef(refId: string): Promise<{ deleted: boolean }> {
  const res = await fetchOrThrow(`/api/artifact-external-refs/${encodeURIComponent(refId)}`, {
    method: 'DELETE',
  });
  return (await res.json()) as { deleted: boolean };
}

export async function supersedeArtifact(
  artifactId: string,
  targetArtifactId: string
): Promise<void> {
  const body: SupersedeAdrBody = { targetArtifactId };
  await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/supersedes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateArtifactBody(
  artifactId: string,
  body: UpdateArtifactBody
): Promise<Artifact> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/body`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as ArtifactResponse;
  return data.artifact;
}

export async function flushArtifactCollaboration(artifactId: string): Promise<void> {
  const url = `/api/artifacts/${encodeURIComponent(artifactId)}/collaboration/flush`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    let body: { code?: string; message?: string } = {};
    try {
      body = (await res.json()) as { code?: string; message?: string };
    } catch {
      // ignore malformed error body
    }
    throw new Error(
      body.message ?? `Flush failed (${String(res.status)}); cannot guarantee snapshot freshness`
    );
  }
}
